import Icon from "@components/Icon";
import { getColours, isEmptyContentfulEntry, richTextHelper } from "@helpers";
import type {
	IModulePlanItemFields,
	IModuleUspFields,
} from "@types-contentful";
import { usePathname } from "next/navigation";
import React from "react";
import styled from "styled-components";

interface WrapperProps {
	blocks: number;
}

const Container = styled.div<{
	resetModuleMarginBottom: "No" | "Yes" | undefined;
	backgroundColour:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
	customPadding: string | undefined;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  padding: 40px 35px 40px 35px;
  padding: ${(props) =>
		props.customPadding === "Yes"
			? "40px 35px 0px 35px"
			: "40px 35px 40px 35px"};
  background-color: ${(props) => getColours(props.backgroundColour)};
  @media (min-width: 834px) {
    padding: ${(props) =>
			props.customPadding === "Yes"
				? "10px 10% 40px 10%"
				: "40px 10% 40px 10%"};
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (min-width: 835px) and (max-width: 1359px) {
	justify-content: center;
    grid-template-columns: repeat(2, 1fr);
	display: grid;
    column-gap: 48px;
  }


  @media (min-width: 834px) {
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: 16px;
    justify-content: space-between;
  }
`;

const Headline = styled.div`
  margin-bottom: 24px;
  @media (min-width: 834px) {
    max-width: 881px;
  }
`;

const BlockWrapper = styled.div<{ hasHeadline: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 16px;
  flex: 1;

  @media (min-width: 834px) {
    flex: 0 1 auto;
    margin-bottom: 0;
		align-items: center;
	}
`;

const IconWrapper = styled.div<WrapperProps>`
  margin-right: 32px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 32px;
    height: 32px;
  }
  @media (min-width: 834px) {
    margin-bottom: 0;
    margin-right: ${(props) => (props.blocks === 2 ? "40px" : "16px")};
  }
  @media (min-width: 1024px) {
    width: 40px;
    height: 40px;
    img {
      width: 40px;
      height: 40px;
    }
  }
`;

const Header = styled.div`
	grid-area: header;
  p {
    font-weight: bold;
    margin-bottom: 16px;
  }
`;
const BodyText = styled.div`
margin: 32px 0;  
`;

const iconSizes = {
	iconMobile: 32,
	iconTablet: 32,
	iconLaptop: 40,
	iconDesktop: 40,
	iconDesktopLarge: 40,
};

const CopyColumn = styled.div<{ blocks: number }>`
  min-width: 0;

  @media (min-width: 834px) {
    ${(p) =>
			p.blocks === 4 &&
			`
    //   flex: 0 0 280px;
    //   width: 280px;
      overflow-wrap: break-word;
    `}
  }
`;

const SubCopy = styled.div`
  @media (min-width: 834px) {
    width: 100%;
  }
`;
interface ContentBlockProps extends IModulePlanItemFields {
	blocks: number;
}
function ContentBlock({
	blocks,
	planItemIconName,
	planItemHeadline,
	planItemText,
}: ContentBlockProps) {
	const hasHeadline = !isEmptyContentfulEntry(planItemHeadline);

	return (
		<BlockWrapper hasHeadline={hasHeadline}>
			<IconWrapper blocks={blocks}>
				<Icon
					iconName={planItemIconName}
					iconSizes={iconSizes}
					alt={planItemIconName}
				/>
			</IconWrapper>
			<CopyColumn blocks={blocks}>
				{hasHeadline && <Header>{richTextHelper(planItemHeadline)}</Header>}
				<SubCopy>
					{!isEmptyContentfulEntry(planItemText) &&
						richTextHelper(planItemText)}
				</SubCopy>
			</CopyColumn>
		</BlockWrapper>
	);
}

const ContentfulModuleUsp = ({
	backgroundColor,
	removePaddingTopAndBottom,
	removeNegativeSpacingBelowModule,
	title,
	bodyText,
	uspItems,
	isThisABanner,
}: IModuleUspFields) => {
	const pathname = usePathname();

	return (
		<>
			<Container
				resetModuleMarginBottom={removeNegativeSpacingBelowModule}
				backgroundColour={backgroundColor}
				customPadding={removePaddingTopAndBottom}
				data-testid={`usp-module-${title || ""}-${bodyText || ""}-${uspItems?.map((item) => item.sys.id).join("-") || ""}-${pathname || ""}`
					.toString()
					.toLowerCase()
					.replace(/\s+/g, "-")}
			>
				{title && (
					<Headline>
						{isThisABanner === "Yes" ? <h1>{title}</h1> : <h2>{title}</h2>}
					</Headline>
				)}
				{bodyText && <BodyText>{bodyText}</BodyText>}
				<InnerContainer>
					{uspItems?.map((uspItem) => (
						<ContentBlock
							key={uspItem.sys.id}
							blocks={uspItems?.length}
							planItemIconName={uspItem.fields.planItemIconName}
							planItemHeadline={uspItem.fields.planItemHeadline}
							planItemText={uspItem.fields.planItemText}
						/>
					))}
				</InnerContainer>
			</Container>
		</>
	);
};

export default ContentfulModuleUsp;
